import { ButtonType, ButtonWidget, DialogType, DialogWidget } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { Messages } from '../../codegen/Localize';
import { CreateCustomDialogFn,ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { UploadInProgressAction, UploadWorkbookActionsTypes } from '../actions/UploadWorkbookActions';
import { UploadWorkbookProgressDialogStyles } from '../styles/UploadWorkbookProgressDialogStyles';
import UploadWorkbookDialogHelper from '../utils/UploadWorkbookDialogHelper';

export type UploadProgressDialogStateProps = Pick<UploadInProgressAction, 'bytesUploaded' | 'totalBytes'> & { uploadStatus: string };
export type UploadProgressDialogOwnProps = {
  id: string;
  totalBytes: number;
  filename: string;
  close: () => void;
  cancelRequest: () => void;
};

export class UploadProgressDialog extends React.PureComponent<UploadProgressDialogStateProps & UploadProgressDialogOwnProps> {

  private formattedTotalBytes: string;

  constructor(props: UploadProgressDialogStateProps & UploadProgressDialogOwnProps) {
    super(props);
    this.formattedTotalBytes = UploadWorkbookDialogHelper.formatBytes(this.props.totalBytes);
  }

  private getFormattedUploadStatus(): string {
    const sent = UploadWorkbookDialogHelper.formatBytes(this.props.bytesUploaded);
    return Messages.contentUpload_uploadWorkbookNameProgressFileStatus({
      SENT: sent,
      TOTAL: this.formattedTotalBytes
    });
  }

  private getPercentage(): number {
    if (this.props.uploadStatus === UploadWorkbookActionsTypes.COMPLETE) {
      return 100;
    }
    const percentageAsInteger = (this.props.bytesUploaded / this.props.totalBytes) * 100;
    return (Math.ceil(percentageAsInteger) / 100) * 90;  // We want to save the last 10% for publish step
  }

  private isPublishing(): boolean {
    return this.props.bytesUploaded === this.props.totalBytes;
  }

  private handleCancel = () => {
    this.props.cancelRequest();
    this.props.close();
  };

  private unloadHandler = (event: any) => {
    event.preventDefault();
    event.returnValue = true;
  };

  componentDidMount = () => {
    window.addEventListener('beforeunload', this.unloadHandler);
  };

  componentWillUnmount(): void {
    window.removeEventListener('beforeunload', this.unloadHandler);
  }

  render() {

    return (

      <div className={UploadWorkbookProgressDialogStyles.contentContainer}>
        <h1 className={UploadWorkbookProgressDialogStyles.title}>{Messages.contentUpload_uploadWorkbookNameProgressTitle()}</h1>
        <h3 className={UploadWorkbookProgressDialogStyles.filename}>{this.props.filename}</h3>
        <div
          className={UploadWorkbookProgressDialogStyles.progressContainer}
          role="progressbar"
          aria-valuenow={this.getPercentage()}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className={UploadWorkbookProgressDialogStyles.progressBar} style={{width: `${this.getPercentage()}%`}}/>
        </div>
        <h4 className={UploadWorkbookProgressDialogStyles.status}>
          {!this.isPublishing() ? this.getFormattedUploadStatus() : Messages.contentUpload_uploadWorkbookNameProgressPublishing()}
        </h4>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId="upload-workbook-dialog-file"
          children={Messages.cancel()}
          handleClick={this.handleCancel}
          disabled={this.isPublishing()}
        />
      </div>

    );
  }
}

const mapStateToProps: MapStateToProps<UploadProgressDialogStateProps, UploadProgressDialogOwnProps> = (state: AppState, ownProps) => {
  const progress = state.uploadWorkbook.uploads.get(ownProps.id);
  return {
    bytesUploaded: progress?.bytesUploaded || 0,
    totalBytes: progress?.totalBytes || ownProps.totalBytes,
    uploadStatus: progress?.type || UploadWorkbookActionsTypes.IN_PROGRESS,
  };
};

const ConnectedUploadProgressDialog = connect(mapStateToProps)(UploadProgressDialog);

/**
 * Options used for ReactModalService
 */
export function UploadProgressDialogOpts(id: string, totalBytes: number, filename: string, cancelRequest: () => void): CreateCustomDialogFn {
  return (closeAndDismiss) => {
    const dialogProps = ReactModalService.getDefaultDialogProps({
      type: DialogType.Uber,
      showCloseButton: false,
      dialogWidth: 400,
      shouldNotCloseOnGlassClick: true,
      disableAutoFocus: true,
      testId: 'upload-workbook-progress'
    }, closeAndDismiss);

    return (
      <DialogWidget {...dialogProps}>
        <ConnectedUploadProgressDialog
          id={id}
          totalBytes={totalBytes}
          filename={filename}
          cancelRequest={cancelRequest}
          close={closeAndDismiss.close}
        />
      </DialogWidget>
    );
  };
}
